import { useEffect, useState, useRef } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';
import HeroImageEditor from '@/components/HeroImageEditor';
import EditableText from '@/components/EditableText';
import { toast } from 'sonner';
import PageIcon from '@/components/PageIcon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Podcasts() {
  const [podcasts, setPodcasts] = useState([]);
  const [currentPodcast, setCurrentPodcast] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const audioRef = useRef(null);
  const heroImage = useHeroImage('podcasts');

  useEffect(() => {
    loadPodcasts();
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
      audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioRef.current.addEventListener('ended', handleEnded);
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        audioRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audioRef.current.removeEventListener('ended', handleEnded);
      }
    };
  }, [currentPodcast]);

  const loadPodcasts = async () => {
    try {
      const response = await axios.get(`${API}/podcasts`);
      setPodcasts(response.data);
    } catch (error) {
      console.error('Failed to load podcasts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const playPodcast = (podcast) => {
    if (!podcast.file_path || podcast.file_path.trim() === '') {
      toast.error('This podcast does not have an audio file yet. Please upload one in the admin panel.');
      return;
    }
    if (currentPodcast?.id === podcast.id) {
      togglePlayPause();
    } else {
      setCurrentPodcast(podcast);
      setIsPlaying(true);
      setTimeout(() => audioRef.current?.play(), 100);
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime += seconds;
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Layout>
      <section className="min-h-screen" data-testid="podcasts-page">
        {/* Large Hero - Acupuncture Needles */}
        <div className="relative h-[400px] overflow-hidden">
          <HeroImageEditor pageName="podcasts" />
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${heroImage})`,
              filter: 'sepia(50%) saturate(120%) hue-rotate(-5deg) brightness(0.6)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#2C1810]/70 via-[#E8934D]/20 to-[#2C1810]/90"></div>
          
          {/* Text Overlay with Icon */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8">
            <PageIcon pageName="podcasts" size="large" />
            <h1 className="text-6xl md:text-8xl retro-title mb-6 text-center" data-testid="page-title"><EditableText as="span" id="podcasts.hero.title">Podcasts</EditableText></h1>
            <p className="text-3xl text-[#F4E4C1] font-bold mb-4 text-center drop-shadow-lg"><EditableText as="span" id="podcasts.hero.subtitle">Conversations on Healing & Wellness</EditableText></p>
            <p className="text-xl text-[#E8934D] font-bold text-center drop-shadow-lg"><EditableText as="span" id="podcasts.hero.location">Broadcasting from Chicago</EditableText></p>
          </div>
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {loading ? (
            <div className="text-center" data-testid="loading-state">
              <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : podcasts.length === 0 ? (
            <div className="text-center cassette-card p-12" data-testid="no-podcasts">
              <p className="text-[#3D2817] text-xl">No podcast episodes yet. Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {podcasts.map((podcast) => (
                <div
                  key={podcast.id}
                  className={`cassette-card p-6 cursor-pointer hover:scale-102 transition-transform ${
                    currentPodcast?.id === podcast.id ? 'ring-4 ring-[#E8B67A]' : ''
                  }`}
                  onClick={() => playPodcast(podcast)}
                  data-testid={`podcast-card-${podcast.id}`}
                >
                  <div className="flex items-start space-x-4">
                    <button
                      className="flex-shrink-0 w-12 h-12 rounded-full bg-[#6B4423] flex items-center justify-center"
                      data-testid={`play-button-${podcast.id}`}
                    >
                      {currentPodcast?.id === podcast.id && isPlaying ? (
                        <Pause className="w-6 h-6 text-[#F4E4C1]" />
                      ) : (
                        <Play className="w-6 h-6 text-[#F4E4C1] ml-1" />
                      )}
                    </button>
                    <div className="flex-1">
                      <h3 className="text-2xl retro-title text-[#3D2817] mb-2" data-testid={`podcast-title-${podcast.id}`}>
                        {podcast.title}
                      </h3>
                      <p className="text-[#3D2817]" data-testid={`podcast-description-${podcast.id}`}>{podcast.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Audio Player */}
          {currentPodcast && (
            <div className="audio-player fixed bottom-0 left-0 right-0 z-50" data-testid="audio-player">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="mb-3">
                  <h4 className="text-[#F4E4C1] font-semibold" data-testid="current-playing-title">{currentPodcast.title}</h4>
                </div>
                
                {/* Progress Bar */}
                <div
                  className="h-2 bg-[#3D2817] rounded-full mb-3 cursor-pointer"
                  onClick={seek}
                  data-testid="progress-bar"
                >
                  <div
                    className="h-full bg-[#E8B67A] rounded-full"
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  ></div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button onClick={() => skip(-10)} className="text-[#F4E4C1] hover:text-[#E8B67A]" data-testid="skip-backward">
                      <SkipBack className="w-6 h-6" />
                    </button>
                    <button
                      onClick={togglePlayPause}
                      className="w-12 h-12 rounded-full bg-[#E8B67A] flex items-center justify-center"
                      data-testid="play-pause-button"
                    >
                      {isPlaying ? (
                        <Pause className="w-6 h-6 text-[#3D2817]" />
                      ) : (
                        <Play className="w-6 h-6 text-[#3D2817] ml-1" />
                      )}
                    </button>
                    <button onClick={() => skip(10)} className="text-[#F4E4C1] hover:text-[#E8B67A]" data-testid="skip-forward">
                      <SkipForward className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="text-[#F4E4C1] text-sm" data-testid="time-display">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentPodcast && currentPodcast.file_path && (
            <audio 
              ref={audioRef} 
              src={`${BACKEND_URL}${currentPodcast.file_path}`}
              onError={(e) => {
                console.error('Audio loading error:', e);
                toast.error('Unable to load podcast audio. Please check the file.');
              }}
            />
          )}
        </div>
      </section>
    </Layout>
  );
}